/**
 * How the review panel reads a lesson's reviews — CURR-55. The API stores each review with
 * the version it read and each finding's outcome (GEN-155, CURR-54); this sorts the newest
 * review into what Eden reads first.
 */

import { forDeveloper } from '@platform/schema/generation';

import type {
  ContentReview,
  FindingOutcome,
  LessonReview,
} from '@platform/schema/generation';

export type GroupedFinding = Pick<
  ContentReview,
  'id' | 'suggestion' | 'outcome'
>;

export const OUTCOME_LABEL: Record<FindingOutcome, string> = {
  FIXED: 'Fixed',
  UNRESOLVED: 'Unresolved',
  DISPUTED: 'Disputed',
  NEW: 'New',
};

/**
 * One review's findings in the panel's three groups. A developer finding goes to its own
 * group whatever its outcome: no rewrite of the words answers it. A Review again finding
 * has no outcome, and is raised by that review.
 */
export function groupFindings<F extends GroupedFinding>(
  findings: readonly F[],
): { judged: F[]; raised: F[]; developer: F[] } {
  const groups = { judged: [] as F[], raised: [] as F[], developer: [] as F[] };
  for (const finding of findings) {
    if (forDeveloper(finding)) groups.developer.push(finding);
    else if (finding.outcome === undefined || finding.outcome === 'NEW')
      groups.raised.push(finding);
    else groups.judged.push(finding);
  }
  return groups;
}

/** "Review 2 · version …a1b2c3d4 · <when>": the tail of the id is what differs. */
export const reviewLabel = (
  review: Pick<LessonReview, 'versionId' | 'reviewedAt'>,
  number: number,
): string =>
  `Review ${String(number)} · version …${review.versionId.slice(-8)} · ${new Date(review.reviewedAt).toLocaleString()}`;
